/**
 * Per-league overlay showing either the league Table or top Player Stats.
 * Triggered by the small Table/Stats buttons rendered next to each league
 * header on the matches list.
 *
 * Backend:
 *   - GET /api/standings/table/:league_name/:country
 *   - GET /api/live-scores/leagues/:league_id/player-stats
 */
import { useEffect, useState } from 'react';
import { useScoresNavigate } from './scoresPath';
import { X, Loader2, BarChart3, Target, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_LIVE = ['1H', '2H', 'ET', 'P', 'HT'];

const LeagueOverlay = ({ open, onClose, league, initialTab = 'table' }) => {
  const navigate = useScoresNavigate();
  const [tab, setTab] = useState(initialTab);
  const [table, setTable] = useState(null);
  const [stats, setStats] = useState(null);
  const [fixtures, setFixtures] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setTab(initialTab); }, [initialTab, open]);

  useEffect(() => {
    if (!open || !league) return;
    setLoading(true);
    if (tab === 'table' && table === null) {
      fetch(`${API_URL}/api/live-scores/leagues/${league.league_id}/standings`)
        .then((r) => (r.ok ? r.json() : { groups: [] }))
        .then((d) => setTable(
          d && Array.isArray(d.groups)
            ? d
            : { grouped: false, groups: Array.isArray(d) ? [{ group: null, rows: d }] : [] }
        ))
        .catch(() => setTable({ grouped: false, groups: [] }))
        .finally(() => setLoading(false));
    } else if (tab === 'stats' && stats === null) {
      fetch(`${API_URL}/api/live-scores/leagues/${league.league_id}/player-stats`)
        .then((r) => (r.ok ? r.json() : { top_scorers: [], top_assists: [] }))
        .then((d) => setStats(d))
        .catch(() => setStats({ top_scorers: [] }))
        .finally(() => setLoading(false));
    } else if (tab === 'fixtures' && fixtures === null) {
      fetch(`${API_URL}/api/live-scores/league/${league.league_id}/upcoming?limit=15`)
        .then((r) => (r.ok ? r.json() : { fixtures: [] }))
        .then((d) => setFixtures(d.fixtures || []))
        .catch(() => setFixtures([]))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [open, league, tab, table, stats, fixtures]);

  // Reset cached data when league changes
  useEffect(() => {
    setTable(null);
    setStats(null);
    setFixtures(null);
  }, [league?.league_id]);

  if (!open || !league) return null;

  const seasonYear = (tab === 'stats' ? stats?.season : table?.season);
  const seasonLabel = seasonYear ? `${seasonYear}/${String((Number(seasonYear) + 1) % 100).padStart(2, '0')}` : null;

  return (
    <div className="fixed inset-0 z-[100] bg-[var(--sk-bg)] overflow-y-auto" data-testid="league-overlay">
      <div className="sticky top-0 z-10 bg-[var(--sk-bg)]/95 backdrop-blur-xl border-b border-[var(--sk-border)]">
        <div className="max-w-lg mx-auto flex items-center gap-3 px-4 py-3">
          {league.logo && <img src={league.logo} alt="" className="w-7 h-7 object-contain" />}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-extrabold truncate">{league.name}</div>
            <div className="text-[11px] text-zinc-500 truncate">{league.country}</div>
          </div>
          {seasonLabel && (tab === 'table' || tab === 'stats') && (
            <span className="shrink-0 text-[10px] font-bold px-2 py-1 rounded-full bg-accent-soft text-accent" data-testid="season-badge">
              {seasonLabel}
            </span>
          )}
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
            aria-label="Close"
            data-testid="league-overlay-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="max-w-lg mx-auto flex gap-1 px-3 pb-2">
          <button
            onClick={() => setTab('table')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[12px] font-bold ${tab === 'table' ? 'bg-accent text-white' : 'bg-white/5 text-zinc-400'}`}
            data-testid="tab-table"
          >
            <BarChart3 className="w-3.5 h-3.5" />
            Table
          </button>
          <button
            onClick={() => setTab('fixtures')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[12px] font-bold ${tab === 'fixtures' ? 'bg-accent text-white' : 'bg-white/5 text-zinc-400'}`}
            data-testid="tab-fixtures"
          >
            <Calendar className="w-3.5 h-3.5" />
            Fixtures
          </button>
          <button
            onClick={() => setTab('stats')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-[12px] font-bold ${tab === 'stats' ? 'bg-accent text-white' : 'bg-white/5 text-zinc-400'}`}
            data-testid="tab-stats"
          >
            <Target className="w-3.5 h-3.5" />
            Stats
          </button>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-3 py-3">
        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-accent" />
          </div>
        )}

        {!loading && tab === 'table' && (
          <TableView
            data={table || { groups: [] }}
            onTeamClick={(teamId) => { if (teamId) { onClose(); navigate(`/team/${teamId}`); } }}
          />
        )}

        {!loading && tab === 'fixtures' && (
          <FixturesView
            fixtures={fixtures || []}
            onMatchClick={(fxId) => { onClose(); navigate(`/match/${fxId}`); }}
            onTeamClick={(teamId) => { onClose(); navigate(`/team/${teamId}`); }}
          />
        )}

        {!loading && tab === 'stats' && (
          <StatsView stats={stats || { top_scorers: [], top_assists: [] }} />
        )}
      </div>
    </div>
  );
};

const TableView = ({ data, onTeamClick }) => {
  const groups = data?.groups || [];
  const hasRows = groups.some((g) => g.rows && g.rows.length);
  if (!hasRows) {
    return <div className="text-center text-sm text-zinc-500 py-12">No standings available yet.</div>;
  }
  return (
    <div className="space-y-4" data-testid="standings-view">
      {groups.map((g, gi) => (
        <div key={g.group || gi}>
          {data.grouped && g.group && (
            <div className="px-1 text-[10px] font-black uppercase tracking-wider text-accent mb-1.5" data-testid={`standings-group-${gi}`}>
              {g.group}
            </div>
          )}
          <TableGrid rows={g.rows || []} onTeamClick={onTeamClick} />
        </div>
      ))}
    </div>
  );
};

const TableGrid = ({ rows, onTeamClick }) => {
  return (
    <div className="rounded-xl bg-[var(--sk-surface)] overflow-hidden">
      <div className="grid grid-cols-[24px_minmax(0,1fr)_28px_28px_28px_36px] gap-1.5 px-3 py-2 text-[10px] font-bold text-zinc-500 uppercase tracking-wider border-b border-[var(--sk-border)]">
        <span>#</span>
        <span>Team</span>
        <span className="text-center">P</span>
        <span className="text-center">GD</span>
        <span className="text-center">W</span>
        <span className="text-center">Pts</span>
      </div>
      {rows.map((row, i) => {
        const pos = row.rank ?? row.position ?? i + 1;
        const name = row.team || row.team_name || row.team?.name || '';
        const logo = row.team_logo || row.team?.logo;
        const teamId = row.team_id || row.team?.id;
        const played = row.played ?? row.all?.played ?? '';
        const gd = row.goal_diff ?? row.goalsDiff ?? row.goals_diff ?? '';
        const wins = row.win ?? row.won ?? row.all?.win ?? '';
        const pts = row.points ?? '';
        return (
          <button
            key={teamId || `${name}-${i}`}
            onClick={() => onTeamClick?.(teamId)}
            className="w-full grid grid-cols-[24px_minmax(0,1fr)_28px_28px_28px_36px] gap-1.5 px-3 py-2 text-[12px] items-center border-b border-[var(--sk-border)]/60 last:border-0 hover:bg-white/[0.03] text-left"
            data-testid={`table-team-${teamId}`}
          >
            <span className="text-zinc-400 font-bold">{pos}</span>
            <div className="flex items-center gap-1.5 min-w-0">
              {logo && <img src={logo} alt="" className="w-3.5 h-3.5 object-contain shrink-0" loading="lazy" />}
              <span className="truncate text-white">{name}</span>
            </div>
            <span className="text-center text-zinc-400 tabular-nums">{played}</span>
            <span className="text-center text-zinc-400 tabular-nums">{gd}</span>
            <span className="text-center text-zinc-400 tabular-nums">{wins}</span>
            <span className="text-center font-black text-white tabular-nums">{pts}</span>
          </button>
        );
      })}
    </div>
  );
};

const FixturesView = ({ fixtures, onMatchClick, onTeamClick }) => {
  if (!fixtures.length) {
    return <div className="text-center text-sm text-zinc-500 py-12">No upcoming fixtures.</div>;
  }
  // Group by date
  const groups = {};
  fixtures.forEach((m) => {
    const day = m.date ? m.date.slice(0, 10) : 'TBD';
    if (!groups[day]) groups[day] = [];
    groups[day].push(m);
  });
  return (
    <div className="space-y-3">
      {Object.entries(groups).map(([day, list]) => {
        const label = day === 'TBD' ? 'TBD' : new Date(day).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' });
        return (
          <div key={day}>
            <div className="px-1 text-[10px] font-black uppercase tracking-wider text-zinc-500 mb-1.5">{label}</div>
            <div className="rounded-xl bg-[var(--sk-surface)] overflow-hidden">
              {list.map((m) => {
                const isLive = STATUS_LIVE.includes(m.status_short);
                const time = m.date ? new Date(m.date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'TBD';
                return (
                  <div
                    key={m.fixture_id}
                    className="flex items-center gap-2 px-3 py-2.5 border-b border-[var(--sk-border)]/60 last:border-0 hover:bg-white/[0.02]"
                  >
                    <div className="w-10 shrink-0 text-center">
                      {isLive ? (
                        <div className="flex flex-col items-center"><span className="live-dot" /><span className="text-[10px] text-[var(--sk-live)] font-bold mt-0.5">{m.elapsed ? `${m.elapsed}'` : 'LIVE'}</span></div>
                      ) : (
                        <span className="text-[10px] text-zinc-400 font-bold">{time}</span>
                      )}
                    </div>
                    <button
                      onClick={() => onMatchClick?.(m.fixture_id)}
                      className="flex-1 min-w-0 text-left"
                      data-testid={`fixtures-match-${m.fixture_id}`}
                    >
                      <div className="flex items-center gap-1.5">
                        {m.home_logo && <img src={m.home_logo} alt="" className="w-4 h-4 object-contain shrink-0" loading="lazy" />}
                        <span className="text-[12px] text-white truncate">{m.home_team}</span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {m.away_logo && <img src={m.away_logo} alt="" className="w-4 h-4 object-contain shrink-0" loading="lazy" />}
                        <span className="text-[12px] text-white truncate">{m.away_team}</span>
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const StatsView = ({ stats }) => {
  const scorers = stats.top_scorers || [];
  const assists = stats.top_assists || [];
  const hasData = scorers.length > 0 || assists.length > 0;
  if (!hasData) {
    return <div className="text-center text-sm text-zinc-500 py-12">No player stats available yet.</div>;
  }
  return (
    <div className="space-y-4">
      {scorers.length > 0 && (
        <section>
          <div className="text-[10px] font-black uppercase tracking-wider text-accent mb-2 px-1">Top Scorers</div>
          <PlayerList rows={scorers} valueKey="goals" valueLabel="G" />
        </section>
      )}
      {assists.length > 0 && (
        <section>
          <div className="text-[10px] font-black uppercase tracking-wider text-accent mb-2 px-1">Top Assists</div>
          <PlayerList rows={assists} valueKey="assists" valueLabel="A" />
        </section>
      )}
    </div>
  );
};

const PlayerList = ({ rows, valueKey, valueLabel }) => (
  <div className="rounded-xl bg-[var(--sk-surface)] overflow-hidden">
    {rows.slice(0, 10).map((p, i) => (
      <div key={p.player_id || i} className="flex items-center gap-2.5 px-3 py-2 border-b border-[var(--sk-border)]/60 last:border-0">
        <span className="w-5 text-center text-[11px] font-bold text-zinc-500">{i + 1}</span>
        {p.photo && <img src={p.photo} alt="" className="w-8 h-8 rounded-full object-cover bg-zinc-800" />}
        <div className="flex-1 min-w-0">
          <div className="text-[12px] text-white truncate">{p.name || p.player_name}</div>
          <div className="text-[10px] text-zinc-500 truncate">{p.team_name || p.team}</div>
        </div>
        <div className="text-right">
          <div className="text-[14px] font-black text-white">{p[valueKey] ?? 0}</div>
          <div className="text-[9px] text-zinc-500 uppercase">{valueLabel}</div>
        </div>
      </div>
    ))}
  </div>
);

export default LeagueOverlay;
